import type { IUiDialogHookCustomContent } from '@experiences/interfaces';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApButton } from '@uipath/portal-shell-react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import urljoin from 'url-join';

import type { ApiFlavor } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    ApiFlavorToConnectorTemplates,
    CustomConnectorTemplate,
    CustomConnectorTemplateTranslationKeys,
    CustomConnectorTemplateUrls,
} from '../../../../services/llm-gateway/LlmGateway.types';
import { getTenantById } from '../../../../services/organization/TenantService';
import { accountLogicalName } from '../../../../store/selectors';

interface CreateCustomConnectorDialogBodyProps extends IUiDialogHookCustomContent {
    apiFlavor?: ApiFlavor;
    restrictToCustomAiGateway?: boolean;
    onConnectorCreationStarted?: (newTab: Window) => void;
    tenantId?: string;
}

const openUrlInNewTab = (url: string): Window | null => {
    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
        return null;
    }

    newTab.opener = null;
    newTab.document.write(`
        <meta name="referrer" content="no-referrer">
        <meta http-equiv="refresh" content="0;url=${url}">
    `);
    newTab.document.close();
    return newTab;
};

const getAvailableTemplates = (apiFlavor?: ApiFlavor, restrictToCustomAiGateway?: boolean): CustomConnectorTemplate[] => {
    if (restrictToCustomAiGateway) {
        return [ CustomConnectorTemplate.CustomAiGateway ];
    }

    if (apiFlavor) {
        return [ ...ApiFlavorToConnectorTemplates[apiFlavor], CustomConnectorTemplate.CustomAiGateway ];
    }

    return Object.values(CustomConnectorTemplate);
};

export const CreateCustomConnectorDialogBody: React.FC<CreateCustomConnectorDialogBodyProps> = ({
    closeDialog, apiFlavor, restrictToCustomAiGateway, onConnectorCreationStarted, tenantId,
}) => {
    const { formatMessage: translate } = useIntl();
    const [ selectedTemplate, setSelectedTemplate ] = useState<CustomConnectorTemplate | null>(null);

    const organizationName = useSelector(accountLogicalName);

    const { data: tenant } = useSWR(
        tenantId ? {
            url: 'tenant',
            id: tenantId,
        } : null,
        getTenantById,
    );

    const availableTemplates = getAvailableTemplates(apiFlavor, restrictToCustomAiGateway);

    const handleCreateConnector = () => {
        if (!selectedTemplate) {
            return;
        }

        const templateUrl = selectedTemplate === CustomConnectorTemplate.CustomAiGateway && apiFlavor
            ? `connectorbuilder_/create?k=template-uipath-aigateway${apiFlavor.toLowerCase()}`
            : CustomConnectorTemplateUrls[selectedTemplate];

        const url = urljoin(
            window.location.origin,
            organizationName,
            tenant?.name ?? '',
            templateUrl
        );

        const newTab = openUrlInNewTab(url);
        if (newTab) {
            onConnectorCreationStarted?.(newTab);
        }

        closeDialog();
    };

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.M}
            p={SpacingToken.M}
        >
            <UiText
                variant={FontVariantToken.fontSizeMBold}
                style={{ textAlign: 'center' }}
            >
                {translate({ id: 'CLIENT_BYO_LLM_CREATE_CUSTOM_CONNECTOR' })}
            </UiText>
            <UiText style={{ textAlign: 'center' }}>
                {translate({ id: 'CLIENT_BYO_LLM_SELECT_TEMPLATE' })}
            </UiText>
            <UiStack
                direction="column"
                gap={SpacingToken.S}
            >
                {availableTemplates.map((template) => (
                    <ApButton
                        key={template}
                        variant={selectedTemplate === template ? 'primary' : 'secondary'}
                        label={translate({ id: CustomConnectorTemplateTranslationKeys[template] })}
                        onClick={() => setSelectedTemplate(template)}
                        style={{ width: '100%' }}
                        data-cy={`template-button-${template}`}
                    />
                ))}
            </UiStack>
            <UiStack
                direction="row"
                justify="end"
                gap={SpacingToken.S}
                pt={SpacingToken.M}
            >
                <ApButton
                    variant="tertiary"
                    label={translate({ id: 'CLIENT_CANCEL' })}
                    onClick={() => closeDialog()}
                    data-cy="cancel-custom-connector-button"
                />
                <ApButton
                    variant="primary"
                    label={translate({ id: 'CLIENT_BYO_LLM_CREATE_CONNECTOR' })}
                    onClick={handleCreateConnector}
                    disabled={!selectedTemplate}
                    data-cy="create-custom-connector-button"
                />
            </UiStack>
        </UiStack>
    );
};
